import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { BtlHelperService } from '@base/services/btl-helper.service';

import { OPTIONS_CONFIG } from '@app/common/app.config';
import { BasicObject } from '@app/common/models/services.interface';
import { BtlPrismeService } from '@app/common/services/btl-prisme.service';
import { ENV_LIST, Options } from '@app/interfaces/config.interface';

import { ConfigurationService, Oauth2Service } from '@common';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, publishLast, refCount } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

/**
 * @todo: Créer une interface pour oauthParams
 */
@Injectable({
    providedIn: 'root',
})
export class Oauth2RessourceServiceBase {
    public static cache: { [index: string]: Observable<any> } = {};
    public inService: boolean = false;
    protected resourceUrl: string = '';
    protected headers: { [index: string]: string } = {};
    private localService: boolean = false;
    private params: HttpParams;
    private overUrl: string = null;
    private noCache: boolean = false;
    private _initialConfig: Options;

    constructor(
        protected oauth2: Oauth2Service,
        protected http: HttpClient,
        protected configService: ConfigurationService,
        protected btlHelper: BtlHelperService,
        protected prismeService: BtlPrismeService,
        @Inject(OPTIONS_CONFIG) protected optionConfig: () => Options,
    ) {
        this._initialConfig = optionConfig();
    }

    public get(checkAuth = true): Observable<any> {
        const key: string = this.getCacheKey();

        if (!this.noCache && Oauth2RessourceServiceBase.cache[key]) {
            return Oauth2RessourceServiceBase.cache[key];
        } else {
            const options: object = this._getRequestOptions();
            Oauth2RessourceServiceBase.cache[key] = this.http.get(this.getUrl(), options).pipe(
                catchError((res: any) => {
                    if (res.status === 401 && checkAuth) {
                        return this.oauth2.autoLogin().pipe(
                            mergeMap((connexion: boolean) => {
                                if (connexion === false) {
                                    this.btlHelper.navigateToExternalURL(this.oauth2.getAuthorizeUrl());
                                    return of(false);
                                } else {
                                    return this.http.get(this.getUrl(), this._getRequestOptions());
                                }
                            }),
                        );
                    }
                    throw res;
                }),
                publishLast(),
                refCount(),
            );
            return Oauth2RessourceServiceBase.cache[key];
        }
    }

    public post(body: object): Observable<any> {
        const key: string = this.getCacheKey(body);

        if (!this.noCache && Oauth2RessourceServiceBase.cache[key]) {
            return Oauth2RessourceServiceBase.cache[key];
        } else {
            Oauth2RessourceServiceBase.cache[key] = this.http.post(this.getUrl(), body, this._getRequestOptions()).pipe(
                catchError((res: any) => {
                    if (res.status === 401) {
                        return this.oauth2.autoLogin().pipe(
                            mergeMap((connexion: boolean) => {
                                if (connexion === false) {
                                    this.btlHelper.navigateToExternalURL(this.oauth2.getAuthorizeUrl());
                                    return of(false);
                                } else {
                                    return this.http.post(this.getUrl(), body, this._getRequestOptions());
                                }
                            }),
                        );
                    }
                    throw res;
                }),
                publishLast(),
                refCount(),
            );
            return Oauth2RessourceServiceBase.cache[key];
        }
    }

    public put(body: { [index: string]: any }): Observable<any> {
        return this.http.put(this.getUrl(), body, this._getRequestOptions()).pipe(
            catchError((res) => {
                if (res.status === 401) {
                    return this.oauth2.autoLogin().pipe(
                        mergeMap((connexion: boolean) => {
                            if (connexion === false) {
                                this.btlHelper.navigateToExternalURL(this.oauth2.getAuthorizeUrl());
                                return of(false);
                            } else {
                                return this.http.put(this.getUrl(), body, this._getRequestOptions());
                            }
                        }),
                    );
                }
                throw res;
            }),
            publishLast(),
            refCount(),
        );
    }

    public delete(): Observable<any> {
        return this.http.delete(this.getUrl(), this._getRequestOptions()).pipe(
            catchError((res) => {
                if (res.status === 401) {
                    return this.oauth2.autoLogin().pipe(
                        mergeMap((connexion: boolean) => {
                            if (connexion === false) {
                                this.btlHelper.navigateToExternalURL(this.oauth2.getAuthorizeUrl());
                                return of(false);
                            } else {
                                return this.http.delete(this.getUrl(), this._getRequestOptions());
                            }
                        }),
                    );
                }
                throw res;
            }),
            publishLast(),
            refCount(),
        );
    }

    public getUrl(): string {
        return this._getRequestUrl() + this.resourceUrl;
    }

    public nd(nd: string): this {
        return this.getResource('/' + nd);
    }

    public eligibilitesPrevisionnelles(): this {
        return this.getResource('/eligibilites-previsionnelles');
    }

    public techno(techno: string): this {
        return this.getResource('/' + techno + '/');
    }

    public postalCode(postalCode: string): this {
        return this.getResource(postalCode + '/');
    }

    public codeInsee(codeInsee: string): this {
        return this.getResource(codeInsee);
    }

    public codeVoie(codeVoie: string): this {
        return this.getResource('/' + codeVoie + '/');
    }

    /**
     * @param resourceUrl
     * @returns
     */
    public setUrl(resourceUrl: string): this {
        const resource: this = this.getInstance();
        resource.resourceUrl += resourceUrl;
        return resource;
    }

    public order(idCommande?: number): this {
        let resourceUrl = '/commande';
        if (idCommande) {
            resourceUrl += '/' + idCommande;
        }
        return this.getResource(resourceUrl);
    }

    public commandes(): this {
        return this.getResource('/commandes-commerciales');
    }

    public panier(id?: number): this {
        return this.getResource(`/paniers${id ? '/' + id : ''}`);
    }

    public ventes(): this {
        return this.getResource('/ventes');
    }

    public modesLivraison(): this {
        return this.getResource('/modes-livraison');
    }

    public setLocalService(flag: boolean = true) {
        const resource: this = this.getInstance();
        resource.localService = flag;
        return resource;
    }

    public ressources(): this {
        return this.getResource('/ressources');
    }

    public repriseMobile(): this {
        return this.getResource('/reprise-mobile');
    }

    public referentielProduits(productId: string): this {
        return this.getResource(`/referentiel/produits/${productId}`);
    }

    public etalementDePaiementRenouvellement(): this {
        return this.getResource('/etalement-de-paiement-renouvellement');
    }

    public numeroTel(): this {
        return this.getResource('/numero-tel');
    }

    public coordonneesContact(): this {
        return this.getResource('/coordonnees-contact');
    }

    public contratsSignes(): this {
        return this.getResource('/contrats-signes');
    }

    public contracts(contractId: string): this {
        return this.getResource('/contrats/' + contractId);
    }

    public personnes(personnesId: number | string): this {
        let resourceUrl = '/personnes';
        if (personnesId) {
            resourceUrl += '/' + personnesId;
        }
        return this.getResource(resourceUrl);
    }

    public equipementsMobiles(): this {
        return this.getResource('/equipements-mobiles');
    }

    public eligibilitePromotionnelle(): this {
        return this.getResource('/eligibilite-promotionnelle');
    }

    public etalementDePaiementTerminalNu(): this {
        return this.getResource('/etalement-de-paiement-terminal-nu');
    }

    public coordonnees(): this {
        return this.getResource('/coordonnees');
    }

    public eligibilitesRenouvellement(): this {
        return this.getResource('/eligibilites-renouvellement');
    }

    public facturation(): this {
        return this.getResource('/adresses-facturation');
    }

    public comptesFacturation(): this {
        return this.getResource('/comptes-facturation');
    }

    public options(): this {
        return this.getResource('/options');
    }

    public avantages(): this {
        return this.getResource('/avantages');
    }

    public abonnement(): this {
        return this.getResource('/abonnement');
    }

    public referentiel(): this {
        return this.getResource('/referentiel');
    }

    public offres(): this {
        return this.getResource('/offres');
    }

    public details(): this {
        return this.getResource('/boutiques/details');
    }

    public lignes(): this {
        return this.getResource('/lignes');
    }

    public paiement(): this {
        return this.getResource('/paiement');
    }

    public produits(): this {
        return this.getResource('/produits');
    }

    public murProduits(): this {
        return this.getResource('/mur-produits');
    }

    public produitsPartenaires(): this {
        return this.getResource('/produits-partenaires');
    }

    public produitsPartenairesCriteres(idPhone?: string): this {
        return this.getResource('/produits-partenaires/' + idPhone);
    }

    public produitsPartenairesDevis(idPhone?: string): this {
        return this.getResource('/produits-partenaires/' + idPhone + '/devis');
    }

    public categories(): this {
        return this.getResource('/categories');
    }

    public clickAndCollect(): this {
        return this.getResource('/click-and-collect');
    }

    public useSalesApi(): this {
        const resource: this = this.getInstance();
        resource.headers['x-version'] = '4';
        return resource;
    }

    public promotions(): this {
        return this.getResource('/promotions');
    }

    public compatibiliteSim(): this {
        return this.getResource('/compatibilite-sim');
    }

    public eligibiliteTokyo(): this {
        return this.getResource('/RsGacEligibilite/eligibilite-tokyo');
    }

    public renouvellementMurForfaits(): this {
        return this.getResource('/renouvellement-mur-forfaits');
    }

    public config(): this {
        return this.getResource('/config');
    }

    public documents(id: string): this {
        return this.getResource('/documents/' + id);
    }

    public setParams(params: { [index: string]: string }): this {
        const resource: this = this.getInstance();
        const search: HttpParams = new HttpParams({ fromObject: params });
        resource.params = search;
        return resource;
    }

    public setNoCache(noCache: boolean): this {
        const resource: this = this.getInstance();
        resource.noCache = noCache;
        return resource;
    }

    public getUserInfo() {
        const resource: Oauth2RessourceServiceBase = this.getInstance();
        return resource.oauth2.jwtPayload;
    }

    public visionImpactMigration() {
        return this.getResource('/visionImpactMigration');
    }

    public getGcpAddress(uuid?: string): this {
        const resource: this = this.getInstance();
        if (uuid) {
            resource.headers['X-Action-Id'] = uuid;
        }
        resource.headers['X-Source'] = 'MAGENTO';
        const resourceUrl = '/referentiel/adresses/propositions-adresse';
        resource.resourceUrl += resourceUrl;
        resource.noCache = false;
        return resource;
    }

    public recommandationTerminaux(): this {
        return this.getResource('/recommandation-terminaux');
    }

    public listerAvantages(): this {
        return this.getResource('/listerAvantages');
    }

    public murContextualise(): this {
        return this.getResource('/catalogue/produits-contextualises');
    }

    public promotionsDisponibles(): this {
        return this.getResource('/promotions-disponibles');
    }

    public optionsSouscriptibles(): this {
        return this.getResource('/options-souscriptibles');
    }

    public offresAdditionnellesSouscriptibles(): this {
        return this.getResource('/offres-additionnelles-souscriptibles');
    }

    public configuration(): this {
        return this.getResource('/configuration');
    }

    public simulateImpactOffers(): this {
        return this.getResource('/simulation-impacts-offres?typeParcours=RENOUVELLEMENT');
    }

    public addHeaders(headers: BasicObject): this {
        const resource: this = this.getInstance();
        resource.headers = Object.assign(resource.headers, headers);
        return resource;
    }

    /**
     * Returns Magento url or B2R url
     * @returns
     * @private
     */
    protected _getRequestUrl(): string {
        if (this.overUrl != null) {
            return this.overUrl;
        }
        return this.localService ? window.ConfigInitial?.oauth?.local : window.ConfigInitial?.oauth?.resourceUrl;
    }

    /**
     * Returns requestion options with params and oauth header
     * @returns
     */
    protected _getRequestOptions(): object {
        this.headers.TrackerId = this.prismeService.getSession();

        if (
            this._initialConfig.environment !== ENV_LIST.PRODUCTION &&
            this._initialConfig.environment !== ENV_LIST.LOCAL
        ) {
            this.headers['x-banc'] = this._initialConfig.oauth.xBanc;
        }
        if (!this.oauth2.access_token) {
            return {
                headers: this.headers,
                params: this.params,
            };
        } else {
            return {
                headers: new HttpHeaders(
                    Object.assign({ Authorization: 'Bearer ' + this.oauth2.access_token }, this.headers),
                ),
                params: this.params,
            };
        }
    }

    protected getInstance(): this {
        if (this.inService) {
            return this;
        } else {
            const instance: this = new Oauth2RessourceServiceBase(
                this.oauth2,
                this.http,
                this.configService,
                this.btlHelper,
                this.prismeService,
                this.optionConfig,
            ) as this;
            instance.inService = true;
            return instance;
        }
    }

    protected getResource(resourceUrl: string) {
        const resource: this = this.getInstance();

        resource.resourceUrl += resourceUrl;
        resource._addSpecialHeader();

        return resource;
    }

    private _addSpecialHeader(): this {
        const resource: this = this.getInstance();

        const xSource = this.configService.getData()?.oauth.xSource || 'MAGENTO';
        const headers: BasicObject = {
            'X-source': xSource,
            'X-Message-Id': uuidv4(),
        };

        resource.addHeaders(headers);

        return resource;
    }

    private getHashCode(params: string[]): string {
        if (params != null && params.length !== 0) {
            return this.stringToHash(JSON.stringify(params)).toString();
        }
    }

    private stringToHash(str: string): number {
        let hash = 5381;
        let i = str.length;
        while (i) {
            hash = (hash * 33) ^ str.charCodeAt(--i);
        }
        /* JavaScript does bitwise operations (like XOR, above) on 32-bit signed
         * integers. Since we want the results to be always positive, convert the
         * signed int to an unsigned by doing an unsigned bitshift. */
        return hash >>> 0;
    }

    private getCacheKey(postBody?: object): string {
        let params: string[] = [];

        params.push(JSON.stringify({ url: this.getUrl() }));

        if (this.params) {
            params = params.concat(JSON.stringify(this.params.keys().map((x) => ({ [x]: this.params.get(x) }))));
        }

        if (!!postBody && Object.values(postBody).length > 0) {
            params.push(JSON.stringify(postBody));
        }

        return this.getHashCode(params);
    }
}
